import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import { AdminHeader } from 'components/AdminHeader';
import { CustomButton, CustomInput, Spinner } from 'components';
import {
  AuthorizedPartner,
  CreateAuthorizedPartnerRequest,
} from 'interfaces';
import { adminAuthorizedPartnersService } from 'services/adminAuthorizedPartnersService';
import { AdminOnlyRoute } from 'utils';

import './AdminAuthorizedPartners.scss';

type PartnerFormValues = {
  companyName: string;
  emailAddress: string;
  homepage: string;
  telephone: string;
};

const emptyForm: PartnerFormValues = {
  companyName: '',
  emailAddress: '',
  homepage: '',
  telephone: '',
};

export const AdminAuthorizedPartners = () => {
  AdminOnlyRoute();

  const [partners, setPartners] = useState<AuthorizedPartner[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isListLoading, setIsListLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const selectedPartner = partners.find((p) => p.id === selectedId) || null;
  const isEditing = Boolean(selectedId);

  const formik = useFormik<PartnerFormValues>({
    initialValues: emptyForm,
    onSubmit: async (values) => {
      setIsSaving(true);
      setError('');

      try {
        const payload: CreateAuthorizedPartnerRequest = {
          companyName: values.companyName.trim() || null,
          emailAddress: values.emailAddress.trim() || null,
          homepage: values.homepage.trim() || null,
          telephone: values.telephone.trim() || null,
        };

        let partner: AuthorizedPartner;

        if (selectedId) {
          partner = await adminAuthorizedPartnersService.update(
            selectedId,
            payload
          );
        } else {
          partner = await adminAuthorizedPartnersService.create(payload);
          setSelectedId(partner.id);
        }

        if (logoFile) {
          partner = await adminAuthorizedPartnersService.uploadLogo(
            partner.id,
            logoFile
          );
          setLogoFile(null);
        }

        await loadPartners();
        setPreviewUrl(partner.logoUrl || '');
      } catch (e) {
        setError('Speichern fehlgeschlagen.');
      } finally {
        setIsSaving(false);
      }
    },
  });

  const loadPartners = async () => {
    setIsListLoading(true);
    setError('');

    try {
      const data = await adminAuthorizedPartnersService.getAll();
      setPartners(data);
    } catch (e) {
      setError('Partner konnten nicht geladen werden.');
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    loadPartners();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      formik.setValues({
        companyName: selectedPartner.name || '',
        emailAddress: selectedPartner.email || '',
        homepage: selectedPartner.homepage || '',
        telephone: selectedPartner.telephone || '',
      });
      setPreviewUrl(selectedPartner.logoUrl || '');
      setLogoFile(null);
    } else {
      formik.setValues(emptyForm);
      setPreviewUrl('');
      setLogoFile(null);
    }
  }, [selectedPartner?.id]);

  const handleSelect = (partner: AuthorizedPartner) => {
    setSelectedId(partner.id);
    setError('');
  };

  const handleNew = () => {
    setSelectedId(null);
    formik.setValues(emptyForm);
    setPreviewUrl('');
    setLogoFile(null);
    setError('');
  };

  const handleDelete = async () => {
    if (!selectedId) {
      return;
    }

    if (!window.confirm('Partner wirklich loeschen?')) {
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await adminAuthorizedPartnersService.remove(selectedId);
      handleNew();
      await loadPartners();
    } catch (e) {
      setError('Loeschen fehlgeschlagen.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!selectedId) {
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const partner = await adminAuthorizedPartnersService.removeLogo(selectedId);
      setPreviewUrl(partner.logoUrl || '');
      setLogoFile(null);
      await loadPartners();
    } catch (e) {
      setError('Logo konnte nicht entfernt werden.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    setLogoFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  return (
    <>
      <AdminHeader />
      <div className="admin-authorized-partners">
        <div className="admin-authorized-partners__header">
          <h1>Authorized Partners</h1>
        </div>

        {error && <div className="admin-authorized-partners__error">{error}</div>}

        <div className="admin-authorized-partners__layout">
          <div className="admin-authorized-partners__list">
            <div className="admin-authorized-partners__list-header">
              <CustomButton text="Neu" width="80px" height="30px" onClick={handleNew} />
            </div>

            {isListLoading ? (
              <div className="admin-authorized-partners__empty">
                <Spinner />
              </div>
            ) : partners.length === 0 ? (
              <div className="admin-authorized-partners__empty">
                Keine Partner vorhanden.
              </div>
            ) : (
              <table className="admin-authorized-partners__table">
                <thead>
                  <tr>
                    <th>Firma</th>
                    <th>E-Mail</th>
                    <th>Telefon</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner) => (
                    <tr
                      key={partner.id}
                      className={selectedId === partner.id ? 'is-selected' : ''}
                      onClick={() => handleSelect(partner)}
                    >
                      <td>{partner.name || '-'}</td>
                      <td>{partner.email || '-'}</td>
                      <td>{partner.telephone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="admin-authorized-partners__form-panel">
            <h2 className="admin-authorized-partners__form-title">
              {isEditing ? 'Partner bearbeiten' : 'Neuer Partner'}
            </h2>

            <div className="admin-authorized-partners__logo-preview">
              {previewUrl ? (
                <img src={previewUrl} alt="Logo" />
              ) : (
                <span>Kein Logo</span>
              )}
            </div>

            <div className="admin-authorized-partners__logo-actions">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleLogoChange}
                disabled={isSaving}
              />
              {isEditing && previewUrl && (
                <CustomButton
                  text="Logo entfernen"
                  width="150px"
                  height="30px"
                  onClick={handleRemoveLogo}
                />
              )}
            </div>

            <form onSubmit={formik.handleSubmit}>
              <div className="admin-authorized-partners__form-field">
                <CustomInput
                  id="companyName"
                  name="companyName"
                  label="Firma"
                  width="100%"
                  value={formik.values.companyName}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="admin-authorized-partners__form-field">
                <CustomInput
                  id="emailAddress"
                  name="emailAddress"
                  label="E-Mail"
                  width="100%"
                  value={formik.values.emailAddress}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="admin-authorized-partners__form-field">
                <CustomInput
                  id="homepage"
                  name="homepage"
                  label="Homepage"
                  width="100%"
                  value={formik.values.homepage}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="admin-authorized-partners__form-field">
                <CustomInput
                  id="telephone"
                  name="telephone"
                  label="Telefon"
                  width="100%"
                  value={formik.values.telephone}
                  onChange={formik.handleChange}
                />
              </div>

              <div className="admin-authorized-partners__form-actions">
                <CustomButton
                  type="submit"
                  text={isSaving ? 'Speichern...' : 'Speichern'}
                  width="120px"
                  height="32px"
                />
                {isEditing && (
                  <CustomButton
                    text="Loeschen"
                    width="100px"
                    height="32px"
                    onClick={handleDelete}
                  />
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};